import { Link } from "react-router-dom";
import { Linkedin, Twitter, Mail, MapPin, Phone } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";

const socialLinks = [
  { name: "LinkedIn", href: "https://linkedin.com", icon: Linkedin },
  { name: "Twitter", href: "https://twitter.com", icon: Twitter },
];

export function Footer() {
  const { t } = useLanguage();
  const currentYear = new Date().getFullYear();

  const marketingLinks = [
    { name: t("nav.smm"), href: "/marketing/smm" },
    { name: t("nav.research"), href: "/marketing/research" },
  ];

  const solutionsLinks = [
    { name: t("nav.crm"), href: "/solutions/crm" },
    { name: t("nav.automation"), href: "/solutions/automation" },
    { name: t("nav.bi"), href: "/solutions/bi-dashboards" },
    { name: t("nav.custom"), href: "/solutions/custom" },
  ];

  const companyLinks = [
    { name: t("footer.privacy"), href: "/privacy" },
    { name: t("footer.terms"), href: "/terms" },
  ];

  return (
    <footer className="bg-primary text-primary-foreground">
      <div className="container py-16">
        {/* Links Section */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
          {/* About Section */}
          <div>
            <Link to="/" className="inline-block mb-4">
              <span className="text-2xl font-bold">Binar+</span>
            </Link>
            <p className="text-primary-foreground/80 mb-6 leading-relaxed text-sm">
              {t("footer.about")}
            </p>
            <div className="flex space-x-4">
              {socialLinks.map((social) => (
                <a
                  key={social.name}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary-foreground/80 hover:text-primary-foreground transition-colors"
                  aria-label={social.name}
                >
                  <social.icon className="h-5 w-5" />
                </a>
              ))}
            </div>
          </div>

          {/* Marketing Links */}
          <div>
            <h3 className="text-lg font-semibold mb-4">{t("nav.marketing")}</h3>
            <ul className="space-y-3">
              {marketingLinks.map((link) => (
                <li key={link.href}>
                  <Link
                    to={link.href}
                    className="text-primary-foreground/80 hover:text-primary-foreground transition-colors text-sm"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Solutions Links */}
          <div>
            <h3 className="text-lg font-semibold mb-4">{t("nav.solutions")}</h3>
            <ul className="space-y-3">
              {solutionsLinks.map((link) => (
                <li key={link.href}>
                  <Link
                    to={link.href}
                    className="text-primary-foreground/80 hover:text-primary-foreground transition-colors text-sm"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-lg font-semibold mb-4">{t("nav.contact")}</h3>
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <Mail className="h-4 w-4 mt-0.5 text-primary-foreground/80" />
                <a
                  href="mailto:hello@binar.uz"
                  className="text-primary-foreground/80 hover:text-primary-foreground transition-colors text-sm"
                >
                  hello@binar.uz
                </a>
              </li>
              <li className="flex items-start gap-3">
                <Phone className="h-4 w-4 mt-0.5 text-primary-foreground/80" />
                <a
                  href="tel:+998901178111"
                  className="text-primary-foreground/80 hover:text-primary-foreground transition-colors text-sm"
                >
                  +998 90 117 81 11
                </a>
              </li>
              <li className="flex items-start gap-3">
                <MapPin className="h-4 w-4 mt-0.5 text-primary-foreground/80" />
                <span className="text-primary-foreground/80 text-sm">
                  {t("footer.address.line1")}<br />
                  {t("footer.address.line2")}<br />
                  {t("footer.address.line3")}
                </span>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-primary-foreground/20 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-primary-foreground/60 text-sm">
              © {currentYear} Binar+. {t("footer.rights")}
            </p>
            <div className="flex gap-6">
              {companyLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className="text-primary-foreground/60 hover:text-primary-foreground text-sm transition-colors"
                >
                  {link.name}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
